export interface NotificationPreference {
  key: string;
  label: string;
  enabled: boolean;
}

interface SuccessResponseOfPreferences {
  data?: NotificationPreference[];
}

interface SuccessResponseOfRecipients {
  data?: string[];
}

export class NotificationPreferenceClient {
  private readonly baseUrl: string;

  constructor(baseUrl: string = '/api/notificationpreferences/me') {
    this.baseUrl = baseUrl;
  }

  private async getHeaders(): Promise<HeadersInit> {
    const token = await window.Clerk?.session?.getToken?.({ template: 'Orderly_default' });
    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  }

  async get(): Promise<NotificationPreference[]> {
    const headers = await this.getHeaders();
    const response = await fetch(this.baseUrl, { method: 'GET', headers });

    if (!response.ok) {
      throw new Error('Failed to load notification preferences');
    }

    const body: SuccessResponseOfPreferences = await response.json();
    return body?.data ?? [];
  }

  async save(preferences: NotificationPreference[]): Promise<NotificationPreference[]> {
    const headers = await this.getHeaders();
    const response = await fetch(this.baseUrl, {
      method: 'PUT',
      headers,
      body: JSON.stringify({
        preferences: preferences.map((p) => ({ key: p.key, enabled: p.enabled })),
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to save notification preferences');
    }

    const body: SuccessResponseOfPreferences = await response.json();
    return body?.data ?? [];
  }

  /**
   * The addresses that should receive a notification at a store, with opt-outs already applied.
   * Use this rather than the unfiltered users-by-role endpoint, which also feeds approver pickers
   * where an opted-out user must still be selectable.
   */
  async getRecipients(notificationType: string, storeNumber: string): Promise<string[]> {
    const headers = await this.getHeaders();
    const url = `/api/notificationpreferences/recipients/${encodeURIComponent(notificationType)}/store/${encodeURIComponent(storeNumber)}`;
    const response = await fetch(url, { method: 'GET', headers });

    if (!response.ok) {
      throw new Error('Failed to load notification recipients');
    }

    const body: SuccessResponseOfRecipients = await response.json();
    return body?.data ?? [];
  }
}

export const notificationPreferenceClient = new NotificationPreferenceClient();
